// Funciones utilitarias básicas que no dependen de bases de datos ni otros módulos complejos

export const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * @description Genera un day_unique_id normalizado con fecha UTC
 * 
 * Esta función es la forma centralizada y canónica de generar day_unique_id.
 * 
 * **Por qué normalizar a medianoche UTC:**
 * - Evita inconsistencias por timezone local
 * - Previene bugs en el cambio de día (00:00)
 * - Garantiza que el mismo día físico genere el mismo ID
 * 
 * **Formato:** YYMMDD (6 dígitos)
 * - YY: Últimos 2 dígitos del año
 * - MM: Mes con padding (01-12)
 * - DD: Día con padding (01-31)
 * 
 * @param {Date} [date=new Date()] - Fecha a convertir (default: ahora)
 * @returns {string} Ejemplo: "250721" para 21/07/2025
 * 
 * @example
 * // Caso normal (fecha actual)
 * const today = generateDayUniqueId()
 * // "250721"
 * 
 * // Caso específico (fecha personalizada)
 * const christmas = generateDayUniqueId(new Date('2025-12-25'))
 * // "251225"
 * 
 * // Caso edge: cambio de día
 * const beforeMidnight = new Date('2025-07-21T23:59:59-03:00') // Argentina
 * const afterMidnight = new Date('2025-07-22T00:00:01-03:00')  // Argentina
 * generateDayUniqueId(beforeMidnight)  // "250722" (ya es 22 en UTC)
 * generateDayUniqueId(afterMidnight)   // "250722" (mismo día UTC)
 */
export function generateDayUniqueId(date: Date = new Date()): string {
    // Normalizar a medianoche UTC para evitar inconsistencias de timezone
    const normalized = new Date(date)
    normalized.setUTCHours(0, 0, 0, 0)
    
    const year = String(normalized.getFullYear()).slice(-2) // "25"
    const month = String(normalized.getMonth() + 1).padStart(2, '0') // "07"
    const day = String(normalized.getDate()).padStart(2, '0') // "21"
    
    return `${year}${month}${day}` // "250721"
}

/**
 * @deprecated Usar generateDayUniqueId() en su lugar
 * @description Genera un ID basado en la fecha actual (AAMMDD)
 * 
 * **⚠️ DEPRECADO:** Esta función usa timezone local y puede causar inconsistencias.
 * Migrar a generateDayUniqueId() que normaliza con UTC.
 * 
 * @returns {string} Ejemplo: "250721" para 21/07/2025
 */
export const generateIdDateNow = (): string => {
    return generateDayUniqueId()
}

/**
 * @param {boolean} local
 * @returns hora string local (arg utc-3) o internacional utc-0
 */
export const getDateNow = (local: boolean = false): string => {
    return local ? new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString() : new Date().toISOString()
}

export const isLocalGeneratedId = (id: string) => {
    // Regex para UUID v4: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
    const uuidV4Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
    return uuidV4Regex.test(id)
}

/**
 * @param id cualquier valor
 * @description valida si el id es un string de 24 caracteres hexadecimales (0-9, a-f)
 * @returns {boolean}
 */
export const isValidMongoId = (id: any): boolean => {
    return typeof id === 'string' && /^[a-fA-F0-9]{24}$/.test(id)
}

export const padCodeComplete = (code: number, cantDigit: number): string => {
    return String(code).padStart(cantDigit, '0')
}

/**
 * Genera day_unique_id con offset de días (para obtener días anteriores/posteriores)
 * 
 * @param {number} daysOffset - Número de días a sumar/restar (negativo para días anteriores)
 * @returns {string} day_unique_id en formato YYMMDD
 * 
 * @example
 * generateDayIdOffset(0)   // "251211" (hoy 11 dic 2025)
 * generateDayIdOffset(-1)  // "251210" (ayer 10 dic)
 * generateDayIdOffset(-7)  // "251204" (hace 1 semana)
 * generateDayIdOffset(1)   // "251212" (mañana)
 */
export function generateDayIdOffset(daysOffset: number = 0): string {
    const date = new Date()
    date.setDate(date.getDate() + daysOffset)
    return generateDayUniqueId(date)
}

/**
 * Genera array de day_unique_ids para últimos N días
 * Útil para queries que necesitan buscar formularios de múltiples días
 * 
 * @param {number} days - Número de días hacia atrás (default: 7)
 * @returns {string[]} Array de day_unique_ids ordenados (más reciente primero)
 * 
 * @example
 * getLastNDayIds(3)  // ['251211', '251210', '251209']
 * getLastNDayIds(7)  // ['251211', '251210', '251209', '251208', '251207', '251206', '251205']
 */
export function getLastNDayIds(days: number = 7): string[] {
    const dayIds: string[] = []
    for (let i = 0; i < days; i++) {
        dayIds.push(generateDayIdOffset(-i))
    }
    return dayIds
}